import React, { forwardRef, useEffect, useRef, useState } from "react";
import {
  DocketAPIAlreadyEnqueuedError,
  DocketAPIError,
  getAPIClient,
  LegalSex,
  legalSexFromString,
} from "../../apiClient";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { infoLog } from "../../utils/logger";
import { IconButton } from "../../components/IconButton/IconButton";
import {
  checkBoxValueMap,
  getLocalizedLegalSexChoices,
  getWhoAmIChoices,
  getWhoAmiRadios,
  SearchFormSchema,
  getWhoAmIChildChoices,
  Radios,
  getSearchRadios,
} from "../../utils/formUtils";
import "./searchForm.css";
import ResponsiveDatePicker from "../../components/form/ResponsiveDatePicker";
import { DateTime } from "luxon";
import SearchFormHeader from "../../components/search/SearchFormHeader";
import lodash from "lodash";
import { ImmunizationNewSearch } from "../../models/Interfaces";
import { readableDate, getDateTime } from "../../utils/data";
import {
  akSearchFooter,
  idSearchFooter,
  mnSearchFooter,
  njSearchFooter,
  utSearchFooter,
} from "../../components/LegalFooters";
import { ErrorModal } from "../../components/modals/ErrorModal";
import {
  enqueuedIzSearchAtom,
  izProviderConfigsAtom,
  izSearchAPIResultAtom,
  selectedSearchAtom,
  userAtom,
  isHydrated,
} from "../../jotai/atoms";
import { useAtom } from "jotai";
import { fetchRecordsUntilUpdated } from "../../utils/polling";
import { SUBDOMAIN, WHITELABEL_KEY } from "../../globals";
import i18n from "../../i18n";
import { LegacyEnqueueSearchRequestDto } from "@docket/consumer-app-client-typescript-axios";

export function SearchForm() {
  const navigate = useNavigate();
  // searches selector to update UI
  const [existingSearch, setExistingSearch] = useAtom(selectedSearchAtom);
  const [submitting, setSubmitting] = useState(false);
  const [user] = useAtom(userAtom);
  const [_izSearchAPIResult, setIzSearchAPIResult] = useAtom(izSearchAPIResultAtom);
  const [_, setEnqueuedIzSearch] = useAtom(enqueuedIzSearchAtom);
  const [providerConfigs] = useAtom(izProviderConfigsAtom);
  const { t } = useTranslation();
  // patient or parent DOB
  const [dob, setDob] = useState<Date | null>(
    existingSearch
      ? getDateTime(existingSearch.dateOfBirth).toJSDate()
      : user?.dob
      ? getDateTime(user.dob).toJSDate()
      : DateTime.now().minus({ years: 18 }).toJSDate()
  );

  // Zipcode state
  const [zipcode, setZipcode] = useState<string>(existingSearch?.zipcode || "");
  const [zipcodeError, setZipcodeError] = useState<string>("");
  const [showZipcode, setShowZipcode] = useState<boolean>(false);
  const [zipcodeRequired, setZipcodeRequired] = useState<boolean>(false);

  // child DOB
  const [cDob, setCDob] = useState<Date | null>(
    existingSearch && existingSearch.childDateOfBirth
      ? getDateTime(existingSearch.childDateOfBirth).toJSDate()
      : new Date()
  );
  // DOB checkbox, to map selection to values
  const [legalSexCheckBoxes, setLegalSexCheckBoxes] = useState(getLocalizedLegalSexChoices(t));
  // Child DOB checkbox, to map selection to values
  const [childLegalSexCheckBoxes, setChildLegalSexCheckBoxes] = useState(
    getLocalizedLegalSexChoices(t)
  );
  // who am I checkbox, to map selection to values
  // ToDo: set initial state from prev search or user object
  const [whoAmICheckBoxes, setWhoAmICheckBoxes] = useState(getWhoAmIChoices(t));
  const [gaveConsent, setGaveConsent] = useState(false);
  const [showChildSearch, setShowChildSearch] = useState(
    existingSearch && existingSearch.childFirstName ? true : false
  );
  const [provider, setProvider] = useState("");
  const providerKey = useRef("");
  const prevProvider = useRef("");

  useEffect(() => {
    if (!isHydrated(providerConfigs)) {
      return;
    }
    // if subdomain equals a state abbreviation exactly
    const supportedSubdomains = providerConfigs.map((config) =>
      config.iz_provider.destination_id.toLowerCase()
    );

    if (supportedSubdomains.includes(SUBDOMAIN)) {
      setProvider(t(`discover.page_${SUBDOMAIN}`) || "");
    } else {
      // NJ whitelabel which does not necessarily equal 'nj'
      const defaultProvider = WHITELABEL_KEY === "nj" ? t(`discover.page_nj`) : "";
      setProvider(defaultProvider || "");
    }

    // Existing search provider should take priority over current subdomain
    if (existingSearch) {
      setProvider(t(`discover.page_${existingSearch.izProviderKey.toLowerCase()}`) || "");
    }
  }, [providerConfigs, existingSearch]);

  // Effect to determine if zipcode is required based on provider
  // Combine the two effects into one

  useEffect(() => {
    if (!provider || provider.length === 0) {
      providerKey.current = "";
      setShowZipcode(false);
      setZipcodeRequired(false);
      return;
    }

    if (!isHydrated(providerConfigs)) {
      return;
    }

    let destination_id = "";
    for (const value of Object.values(providerConfigs)) {
      if (value.enabled && provider.includes(value.iz_provider.destination_id)) {
        destination_id = value.iz_provider.destination_id;
        break;
      }
    }

    if (destination_id.length > 0) {
      providerKey.current = destination_id;
    } else {
      providerKey.current = "";
    }

    const selectedProvider = providerConfigs.find(
      (config) =>
        config.iz_provider.destination_id.toLowerCase() === providerKey.current.toLowerCase()
    );

    const hasZipcode =
      selectedProvider &&
      selectedProvider.additional_fields &&
      selectedProvider.additional_fields.zipcode;

    setShowZipcode(!!hasZipcode);

    const isRequired = hasZipcode && selectedProvider.additional_fields.zipcode.required === true;
    setZipcodeRequired(!!isRequired);
  }, [provider, providerConfigs]);

  const handleZipcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Filter out non-numeric characters and limit to 5 digits
    const filteredValue = e.target.value.replace(/[^0-9]/g, "").slice(0, 5);
    setZipcode(filteredValue);

    if (filteredValue.length === 5 || (!zipcodeRequired && filteredValue.length === 0)) {
      setZipcodeError("");
    }
  };

  const validateZipcode = (value: string) => {
    if (zipcodeRequired && (!value || value.trim() === "")) {
      return t("immunizations.search_check_zipcode_required");
    }

    if (value && !/^\d{5}$/.test(value)) {
      if (!zipcodeRequired && value.trim() === "") {
        // Optional zip code and the field is empty
        // If it ISN'T empty, we still want to validate it.
        return "";
      }
      return t("immunizations.search_check_zipcode_format");
    }
    return "";
  };

  const handleZipcodeBlur = () => {
    const error = validateZipcode(zipcode);
    setZipcodeError(error);
  };

  const checkboxHandler = (value: any, index: number) => {
    const newValue = checkBoxValueMap(legalSexCheckBoxes, index);
    setLegalSexCheckBoxes(newValue);
  };
  // Child DOB checkbox, to map selection to values
  const childCheckboxHandler = (value: any, index: number) => {
    const newValue = checkBoxValueMap(childLegalSexCheckBoxes, index);
    setChildLegalSexCheckBoxes(newValue);
  };
  // who am I checkbox, to map selection to values
  // ToDo: set initial state from prev search or user object
  const whoAmIHandler = (value: any, index: number) => {
    infoLog(`whoAmIHandler ${value} ${index}`);
    const newValue = checkBoxValueMap(whoAmICheckBoxes, index);
    setWhoAmICheckBoxes(newValue);
  };
  const handleConsentChange = (checked: boolean) => {
    setGaveConsent(checked);
  };

  // Handle provider change - reset zipcode when changing providers
  const handleProviderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProvider(e.target.value);
    setZipcode("");
    setZipcodeError("");
  };

  type ErrorModalParams = {
    title: string;
    body: string;
    show: boolean;
  };
  const [showErrorModal, setShowErrorModal] = useState<ErrorModalParams>({
    title: "",
    body: "",
    show: false,
  });
  const [title, message] = t("immunizations.search_check_search_in_queue").split("|");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(SearchFormSchema),
  });

  const searchFormTranslations = {
    firstName: t("immunizations.search_my_first_name_label"),
    lastName: t("immunizations.search_my_last_name_label"),
    dateOfBirth: t("immunizations.search_my_dob_label"),
    legalSex: t("immunizations.search_my_legal_sex_label"),
    zipCode: t("immunizations.search_zip_code"),
    childFirstName: t("immunizations.search_child_first_name_label"),
    childLastName: t("immunizations.search_child_last_name_label"),
    childDateOfBirth: t("immunizations.search_child_dob_label"),
    childLegalSex: t("immunizations.search_child_legal_sex_label"),
    consentTitle: t("immunizations.search_consent_title"),
    consentMessage: t("immunizations.search_consent_message"),
    consentCheckBoxLabel: t("immunizations.i_consent"),
  };

  useEffect(() => {
    if (!provider || provider.length === 0) {
      providerKey.current = "";
      return;
    }

    let destination_id = "";
    for (const value of Object.values(providerConfigs)) {
      if (value.enabled && provider.includes(value.iz_provider.destination_id)) {
        destination_id = value.iz_provider.destination_id;
        break;
      }
    }
    infoLog(`provider '${destination_id}'`);
    if (destination_id.length > 0) {
      providerKey.current = destination_id;
    } else {
      providerKey.current = "";
    }
  }, [provider]);

  useEffect(() => {
    if (existingSearch?.dateOfBirth) {
      setDob(getDateTime(existingSearch.dateOfBirth).toJSDate());
    } else if (user?.dob) {
      setDob(getDateTime(user.dob).toJSDate());
    }
    return () => {};
  }, [existingSearch, user]);

  useEffect(() => {
    if (existingSearch?.zipcode) {
      setZipcode(existingSearch.zipcode);
    }
    return () => {};
  }, [existingSearch]);

  useEffect(() => {
    setLegalSexCheckBoxes(
      checkBoxRenderHelper(legalSexCheckBoxes, [
        existingSearch?.legalSex,
        legalSexFromString(user?.legal_sex || null),
      ])
    );
  }, [existingSearch, user]);

  useEffect(() => {
    setChildLegalSexCheckBoxes(
      checkBoxRenderHelper(childLegalSexCheckBoxes, [existingSearch?.childLegalSex])
    );
  }, [existingSearch]);

  useEffect(() => {
    setWhoAmICheckBoxes(checkBoxRenderHelper(whoAmICheckBoxes, [existingSearch?.whoAmI]));
  }, [existingSearch, user]);

  useEffect(() => {
    // reset was the only way to get hook form to see the form values as valid
    reset({
      firstName: existingSearch?.firstName ?? user?.first_name ?? "",
      lastName: existingSearch?.lastName ?? user?.last_name ?? "",
      childFirstName: existingSearch?.childFirstName ?? "",
      childLastName: existingSearch?.childLastName ?? "",
    });
  }, [existingSearch, user]);

  const onSubmit = async (data: any) => {
    infoLog(`onSubmit ${JSON.stringify(data)}`);
    setSubmitting(true);
    try {
      // This will control the error modal as well, so we don't need to worry about it here.
      const dobsValid = await checkDates();
      //let checkDup = await checkForDuplicateSearch(data)
      const sex = lodash.find(legalSexCheckBoxes, ["checked", true]);
      const csex = lodash.find(childLegalSexCheckBoxes, ["checked", true]);
      const who = lodash.find(whoAmICheckBoxes, ["checked", true]);
      const legalSex = sex?.value ?? null;
      const cLegalSex = csex?.value ?? null;

      // Validate child search data first since it appears at the top of the form
      if (showChildSearch) {
        let errorMsg = "";
        if (!data.childFirstName) {
          errorMsg = t("immunizations.search_check_child_first_name");
        } else if (!data.childLastName) {
          errorMsg = t("immunizations.search_check_child_last_name");
        } else if (!cLegalSex) {
          errorMsg = t("immunizations.search_check_child_legal_sex");
        } else if (!cDob) {
          errorMsg = t("immunizations.search_check_child_dob");
        }

        if (errorMsg.length > 0) {
          setShowErrorModal({
            title: t("immunizations.search_check_header"),
            body: errorMsg,
            show: true,
          });
          return;
        }
      }

      // Add zipcode validation
      if (showZipcode) {
        const zipcodeValidationError = validateZipcode(zipcode);
        if (zipcodeValidationError) {
          setShowErrorModal({
            title: t("immunizations.search_check_header"),
            body: zipcodeValidationError,
            show: true,
          });
          return;
        }
      }

      let errorMsg = "";
      if (!data.firstName) {
        errorMsg = t("immunizations.search_check_first_name");
      } else if (!data.lastName) {
        errorMsg = t("immunizations.search_check_last_name");
      } else if (!legalSex) {
        errorMsg = t("immunizations.search_check_legal_sex");
      } else if (!showChildSearch && !who) {
        // Child searches can only be guardians, so we skip this check since it's auto-selected for the user.
        errorMsg = t("immunizations.search_check_who_am_i");
      } else if (!gaveConsent) {
        errorMsg = t("immunizations.search_check_consent");
      } else if (!dob) {
        errorMsg = t("immunizations.search_check_dob");
      } else if (!providerKey.current || providerKey.current.length == 0) {
        errorMsg = t("immunizations.search_check_provider");
      }

      if (errorMsg.length > 0) {
        setShowErrorModal({
          title: t("immunizations.search_check_header"),
          body: errorMsg,
          show: true,
        });
        return;
      }

      if (dobsValid) {
        await runSearch(data, showChildSearch);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const checkDates = async () => {
    let params: ErrorModalParams = {
      title: "",
      body: "",
      show: false,
    };

    // no child searches if the child is over 18...
    if (
      showChildSearch &&
      cDob &&
      DateTime.now().minus({ years: 18 }) >= DateTime.fromJSDate(cDob)
    ) {
      params = {
        title: t("immunizations.search_age_error_child_title"),
        body: t("immunizations.search_age_error_child_18"),
        show: true,
      };
    }
    // no search if the adult is under 18...
    else if (dob && DateTime.now().minus({ years: 18 }) <= DateTime.fromJSDate(dob)) {
      params = {
        title: t("immunizations.search_age_error_title"),
        body: t("immunizations.search_age_error_18"),
        show: true,
      };
    }

    if (params.show) {
      setShowErrorModal(params);
    }
    return !params.show;
  };

  const runSearch = async (data: ImmunizationNewSearch, childSearch: boolean) => {
    let sex = lodash.find(legalSexCheckBoxes, ["checked", true]);
    let whoami = !showChildSearch
      ? lodash.find(whoAmICheckBoxes, ["checked", true])
      : { value: "guardian" };
    let childLegalSex = showChildSearch
      ? lodash.find(childLegalSexCheckBoxes, ["checked", true])
      : { value: "" };
    data.dateOfBirth = readableDate(dob || "");
    data.legalSex = sex ? sex.value : "U";
    data.childLegalSex = childSearch ? childLegalSex!.value : undefined;
    data.childDateOfBirth = childSearch ? readableDate(cDob || "") : undefined;
    data.childFirstName = childSearch
      ? data.childFirstName
        ? data.childFirstName.trim()
        : ""
      : undefined;
    data.childLastName = childSearch
      ? data.childLastName
        ? data.childLastName.trim()
        : ""
      : undefined;
    data.whoAmI = whoami!.value;
    data.izProviderKey = providerKey.current;
    data.zipcode = showZipcode ? zipcode : undefined;

    let searchObj: LegacyEnqueueSearchRequestDto & { iz_provider_key: string; zipcode?: string } = {
      first_name: data.firstName.trim(),
      last_name: data.lastName.trim(),
      dob: data.dateOfBirth,
      legal_sex: data.legalSex as LegalSex,
      child_first_name: data.childFirstName,
      child_last_name: data.childLastName,
      child_dob: data.childDateOfBirth,
      child_legal_sex: data.childLegalSex as LegalSex,
      who_am_i: data.whoAmI,
      iz_provider_key: data.izProviderKey,
    };

    if (showZipcode && zipcode) {
      searchObj.zipcode = zipcode;
    }

    //setIsSearchRunning(true)
    try {
      const response = await getAPIClient().enqueueIzSearch(searchObj, searchObj.iz_provider_key);
      await setEnqueuedIzSearch(response);
      /**
       * Do not await this since it could potentially take a long time to
       * complete and we want to navigate immediately.
       *
       * Force an immediate refresh so that the new search and loading record
       * appear quickly in the search screen in case the search is initiated
       * while the poller was already running.
       */
      fetchRecordsUntilUpdated(true);
      /**
       * Clear the existing search since we might be creating a new search from
       * a NO_MATCH and we don't want to be taken to the troubleshooting page.
       */
      await setExistingSearch(null);
      navigate("../results", { state: { isScenarioB: false } });
    } catch (e: unknown) {
      if (e instanceof DocketAPIAlreadyEnqueuedError) {
        setShowErrorModal({
          title: t("immunizations.search_check_search_in_queue").split("|")[0],
          body: t("immunizations.search_check_search_in_queue").split("|")[1],
          show: true,
        });
      } else if (e instanceof DocketAPIError) {
        setShowErrorModal({
          title: t("generic.error"),
          body: e.message,
          show: true,
        });
      }
    }
  };

  const runDeleteSearch = async () => {
    if (!existingSearch) {
      return;
    }

    try {
      //      infoLog(`delete search w uid ${existingSearch.uid}`);
      await getAPIClient().deleteIzSearch(existingSearch.uid, existingSearch.izProviderKey);
      const updatedSearches = await getAPIClient().getIzSearches(false);
      await setIzSearchAPIResult(updatedSearches.data);
      setExistingSearch(null);
      navigate(-1);
    } catch (e: unknown) {
      if (e instanceof DocketAPIError) {
        setShowErrorModal({
          title: t("immunizations.search_check_search_not_deleted"),
          body: e.message,
          show: true,
        });
      }
    }
  };

  const getInputField = (
    yupField: "firstName" | "lastName" | "childFirstName" | "childLastName",
    displayName: string,
    defaultVal: string = "",
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void,
    ref?: React.MutableRefObject<HTMLInputElement | null>
  ) => {
    const { ref: registerRef, ...registerRest } = register(yupField);
    return (
      <div key={yupField}>
        <fieldset className="fieldset p-0">
          <legend className="fieldset-legend pt-0 text-[16px] font-[700] text-black dark:text-white max-md:text-form-input-label">
            {`${displayName}*`}
          </legend>
          <input
            {...registerRest}
            data-testid={`input-field-${yupField}`}
            className="input w-full input-responsive-white text-black dark:text-white max-md:text-lg"
            type="text"
            placeholder={displayName}
            defaultValue={defaultVal}
            ref={(el) => {
              registerRef(el);
              if (ref) ref.current = el;
            }}
            onKeyDown={onKeyDown}
          />
        </fieldset>
      </div>
    );
  };

  const getZipcodeField = () => {
    return (
      <div className="flex-1">
        <fieldset className="fieldset p-0">
          <legend className="fieldset-legend pt-0 text-[16px] font-[700] text-dark dark:text-white max-md:text-form-input-label">
            {searchFormTranslations.zipCode}
            {zipcodeRequired && <span className="font-bold">*</span>}
          </legend>
          <input
            data-testid="input-field-zipcode"
            className={`input w-full input-responsive-white text-black dark:text-white ${
              zipcodeError ? "is-danger" : ""
            }`}
            type="text"
            placeholder={searchFormTranslations.zipCode}
            value={zipcode}
            onChange={handleZipcodeChange}
            onBlur={handleZipcodeBlur}
            maxLength={5}
            inputMode="numeric"
          />
        </fieldset>
        {zipcodeError && (
          <p className="help is-danger" data-testid="zipcode-error">
            {zipcodeError}
          </p>
        )}
      </div>
    );
  };

  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);

  const handleKeyDownFirstName = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setTimeout(() => {
        lastNameRef.current?.focus();
      }, 50);
    }
  };

  const handleKeyDownLastName = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      lastNameRef.current?.blur();
    }
  };

  const getParentForm = () => {
    return (
      <>
        {showChildSearch && (
          <p className="max-md:hidden text-xl mb-4 text-black dark:text-white">
            {t("immunizations.search_parent_guardian_header")}
          </p>
        )}
        <div className="flex docket-gap-2 max-md:flex-col">
          <div className="md:flex-1">
            {getInputField(
              "firstName",
              searchFormTranslations.firstName,
              existingSearch?.firstName
                ? existingSearch?.firstName
                : user?.first_name
                ? user.first_name
                : "",
              handleKeyDownFirstName,
              firstNameRef
            )}
          </div>
          <div className="md:flex-1">
            {getInputField(
              "lastName",
              searchFormTranslations.lastName,
              existingSearch?.lastName
                ? existingSearch?.lastName
                : user?.last_name
                ? user.last_name
                : "",
              handleKeyDownLastName,
              lastNameRef
            )}
          </div>
          <div className="md:flex-1 pt-1">
            <p className="text-[16px] font-[700] dark:text-white max-md:text-form-input-label">
              {searchFormTranslations.dateOfBirth} <span className="required-tag">*</span>
            </p>
            <ResponsiveDatePicker selectedDate={dob} setSelectedDate={(d) => setDob(d)} />
          </div>
        </div>

        {showZipcode && (
          <div className="flex w-full docket-gap-2">
            {getZipcodeField()}
            <div className="flex-1 max-md:hidden"></div>
            <div className="flex-1 max-md:hidden"></div>
          </div>
        )}

        {getSearchRadios(
          legalSexCheckBoxes,
          searchFormTranslations.legalSex,
          checkboxHandler,
          "",
          "gender",
          t,
          "max-md:text-form-input-label"
        )}
      </>
    );
  };

  /**
   * We render a lot of check boxes in the form, often needing to ensure a
   * default is selected. This render helper takes the checkboxes to render as
   * well as an ordered list of preferred defaults. If one of the check boxes is
   * already checked, then the checkboxes are returned as is. If none are
   * checked, then the first defined default is selected.
   */
  const checkBoxRenderHelper = (checkboxes: Radios[], possible: (string | undefined)[]) => {
    if (checkboxes.find((box) => box.checked)) {
      return checkboxes;
    }
    const replacement = possible.find((p) => p !== undefined);
    if (replacement) {
      return checkBoxValueMap(
        checkboxes,
        lodash.findIndex(checkboxes, function (item) {
          return item.value === replacement;
        })
      );
    }
    return checkboxes;
  };

  const childFirstNameRef = useRef<HTMLInputElement>(null);
  const childLastNameRef = useRef<HTMLInputElement>(null);

  const handleKeyDownChildFirstName = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      childLastNameRef.current?.focus();
    }
  };

  const handleKeyDownChildLastName = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      childLastNameRef.current?.blur();
    }
  };

  const getChildForm = () => {
    return (
      <>
        <p className="text-dark dark:text-white max-md:hidden text-xl mb-4">
          {t("immunizations.search_child_header")}
        </p>
        <div className="flex max-md:flex-col docket-gap-2">
          <div className="md:flex-1/3">
            {getInputField(
              "childFirstName",
              searchFormTranslations.childFirstName,
              existingSearch?.childFirstName ? existingSearch?.childFirstName : "",
              handleKeyDownChildFirstName,
              childFirstNameRef
            )}
          </div>
          <div className="md:flex-1/3">
            {getInputField(
              "childLastName",
              searchFormTranslations.childLastName,
              existingSearch?.childLastName ? existingSearch?.childLastName : "",
              handleKeyDownChildLastName,
              childLastNameRef
            )}
          </div>
          <div className="md:flex-1/3 pt-1">
            <p className="text-[16px] font-[700] dark:text-white max-md:text-form-input-label">
              {searchFormTranslations.childDateOfBirth} <span className="required-tag">*</span>
            </p>
            <ResponsiveDatePicker selectedDate={cDob} setSelectedDate={(d) => setCDob(d)} />
          </div>
        </div>
        <div>
          {getSearchRadios(
            childLegalSexCheckBoxes,
            searchFormTranslations.childLegalSex,
            childCheckboxHandler,
            "",
            "childGender",
            t,
            "max-md:text-form-input-label"
          )}
          <br />

          <hr />
          <br />
        </div>
      </>
    );
  };

  const getConsentForm = () => {
    return (
      <div key={"consent"}>
        <p className="text-lg font-black text-black dark:text-white max-md:text-form-input-label">
          {searchFormTranslations.consentTitle}
        </p>
        <br />
        <p className="mb-5 text-dark dark:text-white max-md:text-white text-sm">
          {searchFormTranslations.consentMessage}
        </p>
        <label className="fieldset-label text-dark dark:text-white max-md:text-white">
          <input
            className="checkbox text-dark dark:text-white md:!border-gray-500 max-md:border-gray-400 max-md:mr-2 max-md:checked:text-white"
            data-testid="consent-checkbox"
            type="checkbox"
            name={searchFormTranslations.consentCheckBoxLabel}
            checked={gaveConsent}
            onChange={(e) => handleConsentChange(e.target.checked)}
          />
          {searchFormTranslations.consentCheckBoxLabel}
        </label>
      </div>
    );
  };

  return (
    <div className="mb-6">
      {showErrorModal.show && (
        <ErrorModal
          title={showErrorModal.title}
          onCloseModal={() =>
            setShowErrorModal((cur: ErrorModalParams): ErrorModalParams => {
              return { title: cur.title, body: cur.body, show: false };
            })
          }
        >
          <p>{showErrorModal.body}</p>
        </ErrorModal>
      )}
      <div className="md:hidden top-[25px] right-[20px] fixed z-50">
        {!!existingSearch && (
          <IconButton dataTestId="deleteBtn" icon="trash" onClick={runDeleteSearch} alt="delete" />
        )}
      </div>
      <div role="tablist" className="tabs md:hidden flex h-[40px] pt-2">
        <a
          role="tab"
          // Daisy UI tab has a weird sizing issue with tabs where one tab may be slightly larger
          // based on text size and shows a weird bottom border color, despite no border being set.
          className="tab flex-1/2 !text-white h-[42px] font-bold"
          onClick={() => setShowChildSearch(false)}
          style={{
            backgroundColor: showChildSearch ? "#54b2f0" : "#315A80",
          }}
        >
          Adult Records
        </a>
        <a
          role="tab"
          className="tab flex-1/2 !text-white h-[41px] font-bold"
          onClick={() => setShowChildSearch(true)}
          style={{
            backgroundColor: showChildSearch ? "#315A80" : "#54b2f0",
          }}
        >
          Child Records
        </a>
      </div>
      <div className="max-md:bg-form max-md:px-6 max-md:pt-6 max-md:touch-pan-y">
        <div className="max-md:hidden">
          <SearchFormHeader
            deleteSearch={existingSearch ? runDeleteSearch : () => {}}
            showChildSearch={showChildSearch}
            showChild={setShowChildSearch}
            showDelete={!!existingSearch}
          />
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-6 has-text-dark md:px-6">
            <div>
              <p className="text-[16px] font-[700] text-dark dark:text-white pb-1 max-md:text-form-input-label">
                {t("immunizations.search_state_registry_label")}{" "}
                <span className="required-tag">*</span>
              </p>
              <div className="select select-neutral max-md:w-fit bg-white max-md:text-lg max-md:h-[60px]">
                <select
                  data-testid="jurisdiction-selector"
                  value={provider}
                  onChange={handleProviderChange}
                >
                  <option key="select-provider">{t("immunizations.select_provider")}</option>
                  {isHydrated(providerConfigs) &&
                    providerConfigs
                      .sort((a, b) => (a.iz_provider.name < b.iz_provider.name ? -1 : 1))
                      .map((providerConfig) => (
                        <option
                          key={providerConfig.iz_provider.name}
                          value={providerConfig.iz_provider.destination_id}
                        >
                          {i18n.exists(`discover.page_${providerConfig.iz_provider.destination_id}`)
                            ? t(`discover.page_${providerConfig.iz_provider.destination_id}`)
                            : providerConfig.iz_provider.name}
                        </option>
                      ))}
                </select>
              </div>
            </div>
            {showChildSearch && getChildForm()}
            {getParentForm()}
            {showChildSearch
              ? getWhoAmiRadios(getWhoAmIChildChoices(t), t("immunizations.i_am"), whoAmIHandler, t)
              : getWhoAmiRadios(whoAmICheckBoxes, t("immunizations.i_am"), whoAmIHandler, t)}
            <div className="text-xs text-black dark:text-white max-md:text-white">
              *{t("system.required")}
            </div>
            <hr />
            {getConsentForm()}

            <br />
            <button
              type="submit"
              data-testid="search-form-button"
              className="btn docket-button-responsive ml-auto mt-5 !px-5 !py-2 !font-semibold mb-5 w-fit max-md:w-full"
              disabled={submitting}
            >
              {t("immunizations.search_records_button")}
            </button>

            <br />
            <div className="text-black dark:text-white max-md:mb-20 max-md:text-white">
              {provider.includes("Alaska") && akSearchFooter(t)}
              {provider.includes("Idaho") && idSearchFooter(t)}
              {provider.includes("Minn") && mnSearchFooter(t)}
              {provider.includes("New Jersey") && njSearchFooter(t)}
              {provider.includes("Utah") && utSearchFooter(t)}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SearchForm;
